import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Formik, Form, Field, FormikHelpers } from 'formik'
import { useHistory, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { InputField } from '../../../components/Forms'
import styles from './CreateTripActivityPage.module.scss'
import DefaultButton from '../../../components/Shared/Buttons/DefaultButton'
import SelectInput from '../../../components/Forms/SelectField'
import TextareaField from '../../../components/Forms/TextareaField'
import { ActivityCategory, Currency } from '../../../constants/enums'
import classNames from 'classnames'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import GooglePlacesAutocomplete from '../../../components/Forms/GooglePlacesAutocomplete'
import tripService from '../../../supabaseServices/TripService'
import { toFixedISOString } from '../../../utils/format'

interface TripActivityFormValues {
  id?: number
  max_capacity: number | ''
  price: string
  currency: string
  name?: string
  description?: string
  category: string
  start_time: Date | null
  end_time: Date | null
  location?: any
  location_uuid?: string
}

interface SelectOption {
  id: string
  value: string
  label: string
}

const TripActivitySchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  max_capacity: Yup.number().integer().required('Max capacity is required'),
  price: Yup.string().required('Price is required'),
  currency: Yup.string().required('Currency is required'),
  category: Yup.string().required('Category is required'),
  description: Yup.string(),
  start_time: Yup.date().required('Start time is required'),
  end_time: Yup.date()
    .nullable()
    .min(Yup.ref('start_time'), 'End time cannot be earlier than start time'),
  location: Yup.lazy((value) => {
    if (typeof value === 'string') {
      return Yup.string().trim().required('Location is required')
    } else if (typeof value === 'object' && value !== null) {
      return Yup.object().required('Location is required')
    }
    return Yup.mixed().required('Location is required')
  }),
})

interface CreateTripActivityFormProps {
  onSubmit: (values: TripActivityFormValues) => void
  initialValues?: TripActivityFormValues
}

const CreateTripActivityForm: React.FC<CreateTripActivityFormProps> = ({
  onSubmit,
  initialValues,
}) => {
  const history = useHistory()
  const { tripId } = useParams<{ tripId: string }>()
  const [categories, setCategories] = useState<SelectOption[]>([])
  const [currencies, setCurrencies] = useState<SelectOption[]>([])
  const [tripStartDate, setTripStartDate] = useState<Date | undefined>(undefined)
  const [tripEndDate, setTripEndDate] = useState<Date | undefined>(undefined)
  console.log('initialValues', initialValues)
  const handleOnSubmit = async (
    values: TripActivityFormValues,
    { setSubmitting, resetForm }: FormikHelpers<TripActivityFormValues>,
  ) => {
    try {
      const startTimeStr = toFixedISOString(values.start_time)
      const endTimeStr = toFixedISOString(values.end_time)

      let payload: any = { ...values }

      if (startTimeStr) {
        payload.start_time = new Date(startTimeStr)
      } else {
        delete payload.start_time
      }

      if (endTimeStr) {
        payload.end_time = new Date(endTimeStr)
      } else {
        delete payload.end_time
      }

      if (initialValues?.id) {
        payload.id = initialValues.id
      }

      await onSubmit(payload)

      resetForm()
      history.push(`/trips/${tripId}`)
    } catch (error) {
      console.error('Error submitting the form:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const getCurrencyOptions = (setCurrencies: Dispatch<SetStateAction<SelectOption[]>>): void => {
    const currencies = Object.values(Currency).map((activity) => ({
      id: activity,
      value: activity,
      label: activity
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase()),
    }))

    setCurrencies(currencies)
  }

  const getActivityOptions = (setCategories: Dispatch<SetStateAction<SelectOption[]>>): void => {
    const categories = Object.values(ActivityCategory).map((activity) => ({
      id: activity,
      value: activity,
      label: activity
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase()),
    }))

    setCategories(categories)
  }

  useEffect(() => {
    const fetchTripData = async () => {
      try {
        const trip = await tripService.getTripByUUID(tripId)
        if (trip) {
          setTripStartDate(new Date(trip.start_date))
          setTripEndDate(new Date(trip.end_date))
        }
      } catch (error) {
        console.error('Error fetching trip data:', error)
      }
    }

    fetchTripData().then()
    getActivityOptions(setCategories)
    getCurrencyOptions(setCurrencies)
  }, [tripId])

  const inputClasses = classNames(styles.input)

  return (
    <div className={styles.formWrapper}>
      <Formik
        initialValues={{
          category: initialValues?.category || '',
          max_capacity: initialValues?.max_capacity || '',
          price: initialValues?.price || '',
          currency: initialValues?.currency || '',
          name: initialValues?.name || '',
          description: initialValues?.description || '',
          start_time: initialValues?.start_time
            ? new Date(initialValues.start_time)
            : (tripStartDate ?? null),
          end_time: initialValues?.end_time
            ? new Date(initialValues.end_time)
            : (tripEndDate ?? null),
          location: initialValues?.location || null,
          location_uuid: initialValues?.location_uuid || '',
        }}
        validationSchema={TripActivitySchema}
        onSubmit={handleOnSubmit}
      >
        {({ isValid, dirty, values, setFieldValue, errors }) => (
          <Form>
            <div className={styles.row}>
              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Activity Name</p>
                <InputField type="text" name="name" placeholder="Activity Name" />
              </div>
              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Category</p>
                <Field name="category" className={inputClasses}>
                  {({ field, form }: any) => (
                    <SelectInput
                      {...field}
                      form={form}
                      options={categories}
                      className={inputClasses}
                    />
                  )}
                </Field>
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Location</p>
                <Field name="location" className={inputClasses}>
                  {({ field, form }: any) => (
                    <GooglePlacesAutocomplete {...field} form={form} className={inputClasses} />
                  )}
                </Field>
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Price (cents)</p>
                <InputField type="text" name="price" placeholder="Price" required />
              </div>

              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Currency</p>
                <Field name="currency" className={inputClasses}>
                  {({ field, form }: any) => (
                    <SelectInput
                      {...field}
                      form={form}
                      options={currencies}
                      className={inputClasses}
                    />
                  )}
                </Field>
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.rowItem}>
                <p className={styles.rowItemLabel}>Max Capacity</p>
                <InputField type="number" name="max_capacity" placeholder="Max Capacity" required />
              </div>
            </div>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <div className={styles.row}>
                <div className={styles.rowItem}>
                  <p className={styles.rowItemLabel}>Start Time</p>
                  <DateTimePicker
                    disablePast
                    name="start_time"
                    onChange={(date) => setFieldValue('start_time', date)}
                    value={values.start_time}
                    minDateTime={tripStartDate}
                    maxDateTime={tripEndDate}
                  />
                </div>
                <div className={styles.rowItem}>
                  <p className={styles.rowItemLabel}>End Time</p>
                  <DateTimePicker
                    disablePast
                    name="end_time"
                    onChange={(date) => setFieldValue('end_time', date)}
                    value={values.end_time}
                    minDateTime={tripStartDate}
                    maxDateTime={tripEndDate}
                  />
                  {errors.end_time && <div className={styles.errorMsg}>{errors.end_time}</div>}
                </div>
              </div>
            </LocalizationProvider>

            <div className={styles.row}>
              <div className={styles.rowItemFull}>
                <p className={styles.rowItemLabel}>Description</p>
                <Field name="description" className={inputClasses}>
                  {({ field, form }: any) => (
                    <TextareaField {...field} form={form} className={inputClasses} />
                  )}
                </Field>
              </div>
            </div>

            <div className={styles.btnsWrapper}>
              <DefaultButton
                buttonStyles={{ marginRight: '16px' }}
                onClick={() => history.goBack()}
                outlined={true}
                title={'Cancel'}
              />
              <DefaultButton
                disabled={!isValid || !dirty}
                type={'submit'}
                title={initialValues ? 'Update' : 'Create'}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default CreateTripActivityForm
